<template>
  <m-field
    class="is__relative"
    :class="{ 'flex-1': !width }"
    :status="hasError ? 'danger' : 'default'">
    <m-field-label v-if="label">
      {{ label }}

      <help-tooltip v-if="hint" :text="hint" />
    </m-field-label>
    <vuetify-menu
      transition="scale-transition"
      :close-on-content-click="false"
      v-model="showDatePicker"
      :content-class="`date-picker-wrapper date-picker-wrapper-${$.uid}`"
      attach="#date-picker-dialog"
      bottom
      right
      offset-y
      offset-overflow>
      <template #activator="{ attrs }">
        <icon-button
          ref="trigger"
          v-if="iconActivated"
          test-id="date-picker-trigger"
          size="small"
          icon-size="16px"
          v-bind="attrs"
          @click="showDatePicker = true">
          calendar-blank
        </icon-button>
        <text-field
          v-else
          ref="trigger"
          :style="computedWidth"
          class="date-picker-trigger"
          test-id="date-picker-field"
          v-bind="attrs"
          @click="showDatePicker = true"
          :placeholder="$t('forms.elements.datePickers.singleDatePicker.placeholders.selectADate')"
          :model-value="textFieldPlaceholder"
          color="blue"
          readonly
          :trailing-icon="{ icon: 'chevron-down' }"></text-field>
        <m-field-hint :data-testid="`${testId}-errors`" v-if="shouldDisplayMessages">
          {{ errorBucket.join(', ') }}
        </m-field-hint>
      </template>

      <vuetify-date-picker
        hide-header
        ref="datePicker"
        :min="formattedMinDate"
        :max="formattedMaxDate"
        :color="computedColor"
        prev-icon="mdi-chevron-left"
        next-icon="mdi-chevron-right"
        :model-value="localValue"
        @update:model-value="handleDateClick"></vuetify-date-picker>
    </vuetify-menu>
  </m-field>
</template>

<script>
import { VDatePicker as VuetifyDatePicker } from 'vuetify/lib/components/VDatePicker/VDatePicker.mjs';
import { IconButton, TextField, HelpTooltip } from '@/components';
import { VMenu as VuetifyMenu } from 'vuetify/lib/components/VMenu/VMenu.mjs';
import { LuxonDateTimeFormats } from '@nova/core';
import { DateTime } from 'luxon';
import fieldMixin from '@/components/mixins/fieldMixin';
import validatable from '@/components/mixins/validatable';
import testable from '@/components/mixins/testable';
import { isISO8601 } from 'class-validator';
import { useLoggedInCompanyDisplayFormat } from '@/modules/company/composables/useCompanyDisplayFormat';

export default {
  // TODO: Input does not allow readonly mode, so using disabled to prevent manual input.
  // TODO: This could be confusing to users.  MIRANDA NEEDS READONLY FIELDS!
  setup() {
    return {
      companyDisplayFormat: useLoggedInCompanyDisplayFormat()
    };
  },
  components: { IconButton, VuetifyDatePicker, VuetifyMenu, TextField, HelpTooltip },
  mixins: [fieldMixin, validatable, testable],
  props: {
    /**
     * @model
     * Format LuxonDateTimeFormats.DateDashed
     */
    modelValue: {
      type: String,
      required: false
    },
    /**
     * The earliest date that can be selected - leave null to keep it open ended
     * Format LuxonDateTimeFormats.DateDashed
     */
    minDate: {
      type: String,
      required: false,
      default: null
    },
    /**
     * The latest date that can be selected - leave null to keep it open ended
     * Format LuxonDateTimeFormats.DateDashed
     */
    maxDate: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Use the calendar Icon button to activate datepicker rather than text input
     */
    iconActivated: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Format of date to display in the text field. When omitted, uses company dateDisplayFormat.
     */
    displayFormat: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Field label
     */
    label: {
      type: String,
      required: false,
      default: null
    },
    timezone: {
      type: String,
      required: false,
      default: null
    }
  },
  computed: {
    computedColor() {
      return this.mirandaUtil.getTokenCssValue('color-text-primary');
    },
    resolvedDisplayFormat() {
      return (
        this.displayFormat ??
        this.companyDisplayFormat.companyFormatPatterns().dateFormatPattern ??
        LuxonDateTimeFormats.MonthDayYearSlashed
      );
    },
    textFieldPlaceholder() {
      return this.modelValue
        ? DateTime.fromISO(this.modelValue, { zone: this.timezone ?? undefined }).toFormat(
            this.resolvedDisplayFormat,
            {
              locale: this.$i18n.locale
            }
          )
        : null;
    },
    formattedMinDate() {
      let formattedMinDate = this.minDate;
      if (formattedMinDate && isISO8601(formattedMinDate)) {
        formattedMinDate = DateTime.fromISO(this.minDate, {
          zone: this.timezone ?? undefined
        }).toFormat(LuxonDateTimeFormats.DateDashed);
      }
      return formattedMinDate;
    },
    formattedMaxDate() {
      let formattedMaxDate = this.maxDate;
      if (formattedMaxDate && isISO8601(formattedMaxDate)) {
        formattedMaxDate = DateTime.fromISO(this.maxDate, {
          zone: this.timezone ?? undefined
        }).toFormat(LuxonDateTimeFormats.DateDashed);
      }
      return formattedMaxDate;
    },
    localValue() {
      return DateTime.fromISO(this.modelValue).toJSDate() ?? null;
    }
  },
  data() {
    return {
      showDatePicker: false,
      selectedDate: null,
      datePickerWrapperObserver: null,
      initialDialogAttrs: null
    };
  },
  methods: {
    handleDateClick(date) {
      this.internalValue = DateTime.fromJSDate(date).toISODate();
      this.showDatePicker = false;
    },
    attachDatePicker() {
      if (this.showDatePicker) {
        const datePickerDialog = document.getElementById('date-picker-dialog');
        this.$nextTick(() => {
          this.util.attachElToEl({
            el: this.$refs.trigger.$el,
            elToAttach: datePickerDialog,
            elToAttachWidth: 328
          });
          datePickerDialog.showModal();
        });
      }
    }
  },
  watch: {
    showDatePicker(newVal) {
      if (newVal) {
        this.attachDatePicker();
      } else {
        const datePickerDialog = document.getElementById('date-picker-dialog');
        datePickerDialog.close();
      }
    }
  },
  created() {
    window.addEventListener('resize', this.attachDatePicker);
  },
  unmounted() {
    window.removeEventListener('resize', this.attachDatePicker);
  }
};
</script>

<style lang="scss" scoped>
.date-picker-wrapper {
  padding-top: $m-spacing-2;
  padding-bottom: $m-spacing-4;
  background-color: #fff;
  box-shadow: none;
}

:deep(.v-btn--active) {
  background-color: $m-color-text-primary;
  color: $m-color-text-inverted !important;
}
:deep(.v-btn--active .v-btn__content::before) {
  content: ' ';
  height: 1px;
  background-color: white;
  width: 10px;
  position: absolute;
  bottom: -2px;
}

.date-picker-trigger {
  @media (max-width: $tabletBreakpoint) {
    width: 100% !important;
  }
}
</style>
